import { useEffect, useMemo } from 'react';
import { Dispatch } from 'react';
import { isYieldLayer } from '../../Prescription/PrescriptionJob';
import { getScaleItem } from '../LayerStatisticsUtils';
import { getClaasYieldValues, getExatrekYieldValues } from '../../../reducers/YieldReducer';
import {
  DATA_INTEGRATION_CLAAS_CONNECT,
  DATA_INTEGRATION_EXATREK,
} from '../../../reducers/DataIntegrationsReducer';
import { useLangFile } from '../../../context/LanguageContext';
import { getColoringStrategy } from '../../../model/surveys/SurveyColoringStrategies';
import type {
  ColorScaleEntry,
  FarmRef,
  FieldRef,
  LayerStatisticsBucket,
  LayerStatisticsSource,
  SeasonRef,
  YieldDataByField,
} from './LayerStatisticsSourceTypes';
import { findActiveYieldAsset } from '../../../helpers/YieldUtils';

type YieldStatisticsSourceParams = {
  layer: string | null;
  dispatch: Dispatch<any>;
  date: string | null;
  farm: FarmRef | null;
  selectedField: FieldRef | null;
  selectedSeason: SeasonRef | null;
  yieldData: YieldDataByField | null;
};

const emptySource: LayerStatisticsSource = {
  values: null,
  buckets: null,
  summaryData: null,
  classificationData: null,
  useClassificationBuckets: false,
  isReady: false,
  selectedField: null,
};

const useYieldStatisticsSource = ({
  layer,
  dispatch,
  date,
  farm,
  selectedField,
  selectedSeason,
  yieldData,
}: YieldStatisticsSourceParams): LayerStatisticsSource => {
  const LangFile = useLangFile();
  const asset = isYieldLayer(layer)
    ? findActiveYieldAsset(yieldData, selectedField, date, selectedSeason)
    : null;
  const values = asset ? asset.yieldValues : null;
  const assetId = asset ? asset.id : null;
  const assetSource = asset ? asset.source : null;
  const colorIntervals = asset ? asset.colorIntervals : null;

  useEffect(() => {
    if (!isYieldLayer(layer) || !assetId || values || !farm) {
      return;
    }

    if (assetSource === DATA_INTEGRATION_CLAAS_CONNECT) {
      dispatch(getClaasYieldValues(farm.farmId, assetId));
    } else if (assetSource === DATA_INTEGRATION_EXATREK) {
      dispatch(getExatrekYieldValues(assetId));
    }
  }, [assetId, assetSource, dispatch, farm, layer, values]);

  return useMemo<LayerStatisticsSource>(() => {
    if (!isYieldLayer(layer) || !asset || !values) {
      return emptySource;
    }
    const strategy = getColoringStrategy(
      layer,
      null,
      false,
      false,
      values,
      undefined,
      colorIntervals
    );
    const scale = strategy.scale || null;

    if (!scale) {
      return emptySource;
    }

    const buckets: LayerStatisticsBucket[] = scale.map(
      (scaleEntry: ColorScaleEntry, idx: number, arr: ColorScaleEntry[]) => {
        const scaleItem = { ...scaleEntry, color: scaleEntry.color.toString() };
        return {
          item: getScaleItem(scaleItem, idx, arr, true, 2, LangFile.MapLegend.yieldUnit),
          min: Number(scaleEntry.min),
          max: Number(scaleEntry.max),
          count: 0,
        };
      }
    );

    return {
      values,
      buckets,
      summaryData: null,
      classificationData: null,
      useClassificationBuckets: false,
      isReady: true,
      selectedField,
    };
  }, [LangFile, asset, layer, values, colorIntervals, selectedField]);
};

export default useYieldStatisticsSource;
